import { useState, useRef, useEffect } from "react";
import { api } from "../lib/api";
import { t } from "../i18n/translations";
import type { Language } from "../i18n/translations";

interface Props {
  projectId: string;
  language?: Language;
}

export default function QuickFeedbackButton({ projectId, language = "en" }: Props) {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [sending, setSending] = useState(false);
  const [thanks, setThanks] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  async function handleSubmit() {
    if (!feedback.trim() || sending) return;
    setSending(true);
    try {
      await api.submitQuickFeedback(projectId, feedback.trim());
      setFeedback("");
      setThanks(true);
      setTimeout(() => {
        setThanks(false);
        setOpen(false);
      }, 2000);
    } catch {
      // Silently fail -- don't break UX for feedback
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed bottom-20 right-6 z-50">
      {/* Popover */}
      {open && (
        <div
          ref={popoverRef}
          className="absolute bottom-14 right-0 w-72 bg-slate-800 border border-slate-600 rounded-2xl shadow-xl p-4 animate-fade-in"
        >
          {thanks ? (
            <p className="text-green-400 text-sm font-medium text-center py-2">
              {t("feedback.quick.thanks", language)}
            </p>
          ) : (
            <>
              <p className="text-sm text-slate-300 mb-2">
                {t("feedback.quick.title", language)}
              </p>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder={t("feedback.quick.placeholder", language)}
                rows={3}
                className="w-full bg-slate-900 border border-slate-600 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:border-blue-500"
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleSubmit}
                  disabled={!feedback.trim() || sending}
                  className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-xl px-4 py-1.5 text-sm transition-colors"
                >
                  {sending
                    ? t("feedback.quick.sending", language)
                    : t("feedback.quick.send", language)}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Floating button */}
      <button
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white rounded-full shadow-lg px-4 py-3 transition-colors"
        title={t("feedback.quick.button", language)}
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        <span className="text-sm font-medium">{t("feedback.quick.button", language)}</span>
      </button>
    </div>
  );
}
